<template>
  <AwTooltip v-if="config" :max-width="500">
    <AwBadge :label="config.badge.label" :leading-icon="config.badge.icon" :color="config.badge.color" is-rounded />
    <template #content>
      <div class="flex flex-col gap-1">
        <div v-for="(line, index) in config.tooltipLines" :key="index" class="flex items-center gap-1.5">
          <AwIcon :name="line.icon" :class="[line.iconClass, 'size-4 shrink-0']" />
          <span>{{ line.text }}</span>
        </div>
      </div>
    </template>
  </AwTooltip>
</template>

<script setup lang="ts">
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import type { DocumentLifecycleStatus } from "@asap/shared/schemas/document.schemas";
import type { DocumentInternalReview, DocumentExternalReview } from "@asap/shared/schemas/document-review.schemas";

export type Props = {
  lifecycleStatus?: DocumentLifecycleStatus;
  daysBeforeExpiration?: number | null;
  expirationDate?: string | null;
  internalReviews?: DocumentInternalReview[];
  externalReviews?: DocumentExternalReview[];
};

const props = defineProps<Props>();

type BadgeConfig = {
  label: string;
  icon: string;
  color: "success" | "caution" | "critical";
};

type TooltipLine = {
  icon: string;
  iconClass: string;
  text: string;
};

const EXTERNAL_VALIDATOR_LABELS: Record<string, string> = {
  titre_de_sejour_prefecture_police_paris: "la préfecture de police de Paris",
};

const buildReviewLines = (): TooltipLine[] => {
  const lines: TooltipLine[] = [];

  for (const review of props.internalReviews ?? []) {
    const date = format(new Date(review.created_at), "dd/MM/yy");
    const reviewer = review.reviewer_name;
    switch (review.status) {
      case "approved":
        lines.push({
          icon: "i-ph-check-circle-fill",
          iconClass: "text-success-600",
          text: reviewer ? `Validé par ${reviewer} le ${date}` : `Validé le ${date}`,
        });
        break;
      case "in_progress":
        lines.push({ icon: "i-ph-hourglass-low-fill", iconClass: "text-yellow-500", text: "En cours de validation" });
        break;
      case "rejected": {
        const base = reviewer ? `Rejeté par ${reviewer} le ${date}` : `Rejeté le ${date}`;
        const text = review.rejection_reason ? `${base} pour : ${review.rejection_reason}` : base;
        lines.push({
          icon: "i-ph-x-circle-fill",
          iconClass: "text-critical-600",
          text,
        });
        break;
      }
      case "failed":
        lines.push({
          icon: "i-ph-x-circle-fill",
          iconClass: "text-critical-600",
          text: reviewer ? `La validation par ${reviewer} a échoué le ${date}` : `Échoué le ${date}`,
        });
        break;
    }
  }

  for (const review of props.externalReviews ?? []) {
    const date = format(new Date(review.created_at), "dd/MM/yy");
    const validator = EXTERNAL_VALIDATOR_LABELS[review.reviewer] ?? review.reviewer;
    switch (review.status) {
      case "approved":
        lines.push({
          icon: "i-ph-check-circle-fill",
          iconClass: "text-success-600",
          text: `Validé par ${validator} le ${date}`,
        });
        break;
      case "in_progress":
        lines.push({
          icon: "i-ph-hourglass-low-fill",
          iconClass: "text-yellow-500",
          text: `En cours de validation par ${validator}`,
        });
        break;
      case "rejected": {
        const base = `Rejeté par ${validator} le ${date}`;
        const text = review.rejection_reason ? `${base} pour : ${review.rejection_reason}` : base;
        lines.push({
          icon: "i-ph-x-circle-fill",
          iconClass: "text-critical-600",
          text,
        });
        break;
      }
      case "failed":
        lines.push({
          icon: "i-ph-x-circle-fill",
          iconClass: "text-critical-600",
          text: `Échoué par ${validator} le ${date}`,
        });
        break;
    }
  }

  return lines;
};

const config = computed<{ badge: BadgeConfig; tooltipLines: TooltipLine[] } | null>(() => {
  switch (props.lifecycleStatus) {
    case "required":
      return {
        badge: { label: "À valider", icon: "i-ph-x-circle-fill", color: "critical" },
        tooltipLines: [
          { icon: "i-ph-x-circle-fill", iconClass: "text-critical-600", text: "Le document n'a pas encore été validé" },
        ],
      };

    case "approved": {
      const label = props.expirationDate
        ? `Valide jusqu'au ${format(new Date(props.expirationDate), "dd/MM/yyyy")}`
        : "Valide";
      return {
        badge: { label, icon: "i-ph-check-circle-fill", color: "success" },
        tooltipLines: buildReviewLines(),
      };
    }

    case "in_progress":
      return {
        badge: { label: "En cours de validation", icon: "i-ph-hourglass-low-fill", color: "caution" },
        tooltipLines: buildReviewLines(),
      };

    case "rejected":
      return {
        badge: { label: "Rejeté", icon: "i-ph-x-circle-fill", color: "critical" },
        tooltipLines: buildReviewLines(),
      };

    case "failed":
      return {
        badge: { label: "Échoué", icon: "i-ph-x-circle-fill", color: "critical" },
        tooltipLines: buildReviewLines(),
      };

    case "expiring_soon": {
      const days = props.daysBeforeExpiration;
      const label = days != null ? `Expire dans ${days} jour${days > 1 ? "s" : ""}` : "Expire bientôt";
      const tooltipLines = buildReviewLines();
      if (props.expirationDate) {
        tooltipLines.push({
          icon: "i-ph-warning-fill",
          iconClass: "text-yellow-500",
          text: `Expire le ${format(new Date(props.expirationDate), "dd MMMM yyyy", { locale: fr })}`,
        });
      }
      return {
        badge: { label, icon: "i-ph-warning-fill", color: "caution" },
        tooltipLines,
      };
    }

    case "expired": {
      const tooltipLines: TooltipLine[] = [];
      if (props.expirationDate) {
        tooltipLines.push({
          icon: "i-ph-x-circle-fill",
          iconClass: "text-critical-600",
          text: `Expirée depuis le ${format(new Date(props.expirationDate), "dd/MM/yy")}`,
        });
      }
      return {
        badge: { label: "Expiré", icon: "i-ph-x-circle-fill", color: "critical" },
        tooltipLines,
      };
    }

    default:
      return null;
  }
});
</script>
